import { useCallback, useMemo } from "react";

import moment from "moment-timezone";

import { DATE_FORMAT } from "../constants/global.constants";

/**
 * Custom hook for handling timezone conversions and date formatting.
 * Detects user's timezone and allows override from Redux store.
 * Provides utilities for converting and formatting dates in the correct timezone.
 *
 * @returns {{
 *   timezone: string - The current timezone (from user preferences or auto-detected),
 *   formatTime: Function - Converts and formats dates to the current timezone
 * }}
 */
const useTimezone = () => {
  const userTimezone = useMemo(() => moment.tz.guess(), []);

  // currently, in RO we doesn't have user preference for timezone, but might add in future.
  // const timezone = useSelector(state => state.userInfo?.data?.timeZone) || userTimezone;
  const timezone = userTimezone;

  /**
   * Converts a date to the current timezone and optionally formats it.
   * Handles invalid dates and errors by returning the original input.
   *
   * @param {string|Date} date - The date to convert
   * @param {boolean} [isFormat=true] - Whether to return formatted string or moment object
   * @param {string} [format=DATE_FORMAT] - The format string to use if formatting
   * @returns {string|moment.Moment} Formatted date string or moment object
   */
  const formatTime = useCallback(
    (date, isFormat = true, format = DATE_FORMAT) => {
      if (!date) {
        return date;
      }

      try {
        const convertedDate = moment.utc(date).tz(timezone);

        if (!convertedDate.isValid()) {
          return date;
        }

        return isFormat ? convertedDate.format(format) : convertedDate;
      } catch (error) {
        return date;
      }
    },
    [timezone],
  );

  return {
    timezone,
    formatTime,
  };
};

export default useTimezone;
